import { useState } from 'react';
import { ArrowDown, ArrowUp, Check, ChevronDown, ChevronUp, Loader2, Pencil, Sparkles, Trash2, X } from 'lucide-react';
import type { ReportDraftSection } from '@officehub/shared';
import type { SectionEditorProps } from './types';
import { htmlToText, sanitizeHtml, textToHtml } from './sanitize';
import { CARD, INPUT, PRIMARY, SECONDARY, TINT, DANGER } from './ui';

interface Props {
  section: ReportDraftSection;
  isFirst: boolean;
  isLast: boolean;
  isOnline: boolean;
  isRewriting: boolean;
  renderSectionEditor?: (props: SectionEditorProps) => React.ReactNode;
  onUpdate(id: string, updates: Partial<ReportDraftSection>): void;
  onDelete(id: string): void;
  onMove(id: string, direction: -1 | 1): void;
  onRewrite(id: string, content: string, instructions: string): Promise<void>;
}

const ICON_BTN = 'inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100 disabled:opacity-30';

export function SectionCard(p: Props) {
  const { section } = p;
  const [collapsed, setCollapsed] = useState(false);
  const [editing, setEditing] = useState(false);
  // With a host editor the draft is HTML; with the plain editor it is text.
  const [editValue, setEditValue] = useState('');
  const [showRewrite, setShowRewrite] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const startEdit = () => {
    setEditValue(p.renderSectionEditor ? section.content : htmlToText(section.content));
    setEditing(true);
  };
  const saveEdit = () => {
    p.onUpdate(section.id, { content: p.renderSectionEditor ? editValue : textToHtml(editValue) });
    setEditing(false);
  };
  const rewrite = async () => {
    if (!instructions.trim()) return;
    await p.onRewrite(section.id, section.content, instructions.trim());
    setShowRewrite(false);
    setInstructions('');
  };

  return (
    <div className={`${CARD} space-y-3`}>
      <div className="flex items-center gap-1">
        <input
          type="text"
          aria-label="Section title"
          value={section.title}
          onChange={(e) => p.onUpdate(section.id, { title: e.target.value })}
          className="min-h-[44px] flex-1 rounded-lg bg-transparent px-1 text-sm font-bold text-slate-900 focus:outline-none focus:ring-1 focus:ring-[#3FA5FF]/40"
        />
        <button type="button" aria-label="Move section up" disabled={p.isFirst} onClick={() => p.onMove(section.id, -1)} className={ICON_BTN}>
          <ArrowUp size={16} />
        </button>
        <button type="button" aria-label="Move section down" disabled={p.isLast} onClick={() => p.onMove(section.id, 1)} className={ICON_BTN}>
          <ArrowDown size={16} />
        </button>
        <button type="button" aria-label={collapsed ? 'Expand section' : 'Collapse section'} onClick={() => setCollapsed(!collapsed)} className={ICON_BTN}>
          {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
      </div>

      {!collapsed && (
        <>
          {editing ? (
            <div className="space-y-2">
              {p.renderSectionEditor ? (
                p.renderSectionEditor({ value: editValue, onChange: setEditValue, placeholder: 'Write section content...' })
              ) : (
                <textarea
                  autoFocus
                  aria-label="Section text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  rows={6}
                  className={`${INPUT} resize-none`}
                />
              )}
              <div className="flex gap-2">
                <button type="button" onClick={saveEdit} className={PRIMARY}><Check size={14} /> Save</button>
                <button type="button" onClick={() => setEditing(false)} className={SECONDARY}><X size={14} /> Cancel</button>
              </div>
            </div>
          ) : (
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed text-slate-700"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
            />
          )}

          {showRewrite && (
            <div className="space-y-2 rounded-xl bg-[#E6F2FB] p-3">
              <input
                type="text"
                autoFocus
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !p.isRewriting && p.isOnline) void rewrite(); }}
                placeholder="e.g. 'make it shorter' or 'add detail about access'"
                className={INPUT}
              />
              <div className="flex gap-2">
                <button type="button" onClick={() => void rewrite()} disabled={!p.isOnline || p.isRewriting || !instructions.trim()} className={PRIMARY}>
                  {p.isRewriting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Rewrite
                </button>
                <button type="button" onClick={() => { setShowRewrite(false); setInstructions(''); }} className={SECONDARY}>Cancel</button>
              </div>
            </div>
          )}

          {!editing && !showRewrite && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={startEdit} className={SECONDARY}><Pencil size={13} /> Edit</button>
              <button type="button" onClick={() => setShowRewrite(true)} disabled={!p.isOnline} className={TINT}><Sparkles size={13} /> AI Rewrite</button>
              {confirmDelete ? (
                <div className="ml-auto flex gap-2">
                  <button type="button" aria-label="Confirm delete" onClick={() => p.onDelete(section.id)} className={DANGER}>Delete</button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className={SECONDARY}>Keep</button>
                </div>
              ) : (
                <button type="button" aria-label="Delete section" onClick={() => setConfirmDelete(true)} className={`${ICON_BTN} ml-auto active:text-red-600`}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
